import type { RouteRecordRaw } from "vue-router";
import AboutPage from "../components/AboutPage.vue";
import BootLoading from "../components/BootLoading.vue";
import EditorShell from "../components/EditorShell.vue";
import SplashPage from "../components/SplashPage.vue";
import StoryboardPage from "../components/StoryboardPage.vue";
import EntityGraphCanvas from "../components/EntityGraphCanvas.vue";
import UiGallery from "../components/ui/UiGallery.vue";
import DocumentEditor from "../components/DocumentEditor.vue";
import ManuscriptPane from "../components/ManuscriptPane.vue";
import MapEditorHost from "../components/MapEditorHost.vue";
import OutlineTree from "../components/outline/OutlineTree.vue";
import OutlineNotes from "../components/outline/OutlineNotes.vue";
import OutlineDatabase from "../components/outline/OutlineDatabase.vue";
import OutlineTrash from "../components/outline/OutlineTrash.vue";
import StoryboardPanel from "../components/outline/StoryboardPanel.vue";
import EditorContextPane from "../components/EditorContextPane.vue";
import ContextEmpty from "../components/context/ContextEmpty.vue";
import ManuscriptContextPane from "../components/context/ManuscriptContextPane.vue";

/**
 * Hash-mode route table. Editor chrome lives at `/` (not `/editor/…`) so the
 * hosted URL can be `…/editor/<uuid>/#/database/entry/Rome`. Shell surfaces
 * (`/splash`, `/about`, `/ui`, `/boot`) sit alongside. Static segments
 * (`map/:path`, `database/…`, etc.) are declared before `/:chapter/:scene` so
 * chapter names cannot shadow them.
 *
 * Each project child declares named views: `default` (main), `outline`
 * (left), `context` (right).
 */
const manuscriptViews = {
  default: ManuscriptPane,
  outline: OutlineTree,
  context: ManuscriptContextPane,
};

const notesViews = {
  default: DocumentEditor,
  outline: OutlineNotes,
  context: ContextEmpty,
};

const trashViews = {
  default: DocumentEditor,
  outline: OutlineTrash,
  context: ContextEmpty,
};

const databaseListViews = {
  default: DocumentEditor,
  outline: OutlineDatabase,
  context: ContextEmpty,
};

const databaseGraphViews = {
  default: EntityGraphCanvas,
  outline: OutlineDatabase,
  context: ContextEmpty,
};

const databaseEntryViews = {
  default: DocumentEditor,
  outline: OutlineDatabase,
  context: EditorContextPane,
};

const databaseMapViews = {
  default: MapEditorHost,
  outline: OutlineDatabase,
  context: ContextEmpty,
};

const storyboardViews = {
  default: StoryboardPage,
  outline: StoryboardPanel,
  context: EditorContextPane,
};

export const routes: RouteRecordRaw[] = [
  { path: "/boot", name: "boot", component: BootLoading },
  { path: "/splash", name: "splash", component: SplashPage },
  { path: "/ui", name: "ui-gallery", component: UiGallery },
  { path: "/about", name: "about", component: AboutPage },
  {
    path: "/",
    component: EditorShell,
    props: (route) => ({ demo: route.query.demo === "1" }),
    meta: { editor: true },
    children: [
      {
        path: "",
        name: "manuscript",
        components: manuscriptViews,
        meta: { section: "manuscript" },
      },
      {
        path: "search",
        redirect: { name: "manuscript" },
      },
      {
        path: "storyboard",
        name: "storyboard",
        components: storyboardViews,
        meta: { section: "storyboard" },
      },
      {
        path: "notes",
        name: "notes",
        components: notesViews,
        meta: { section: "notes" },
      },
      {
        path: "notes/:name",
        name: "notes-item",
        components: notesViews,
        meta: { section: "notes" },
      },
      {
        path: "database",
        name: "database",
        components: databaseListViews,
        meta: { section: "database" },
      },
      {
        path: "database/graph",
        name: "database-graph",
        components: databaseGraphViews,
        meta: { section: "database" },
      },
      {
        path: "database/entry/:name",
        name: "database-entry",
        components: databaseEntryViews,
        meta: { section: "database" },
      },
      {
        path: "database/map/:name",
        name: "database-map",
        components: databaseMapViews,
        meta: { section: "database" },
      },
      {
        path: "trash",
        name: "trash",
        components: trashViews,
        meta: { section: "trash" },
      },
      {
        path: "trash/:name",
        name: "trash-item",
        components: trashViews,
        meta: { section: "trash" },
      },
      {
        // Manuscript map figure preview (#266). Encoded project-relative
        // `.zwmap` path; must stay before `/:chapter/:scene`.
        path: "map/:path",
        name: "manuscript-map",
        components: manuscriptViews,
        meta: { section: "manuscript" },
      },
      {
        path: ":chapter/:scene",
        name: "manuscript-scene",
        components: manuscriptViews,
        meta: { section: "manuscript" },
      },
    ],
  },
];
